import ClearIcon from '@mui/icons-material/Clear';
import { Box, FormControlLabel, IconButton, MenuItem, Switch, TextField } from '@mui/material';
import { add, addYears } from 'date-fns';
import { useFormik } from 'formik';
import { forwardRef, useCallback, useEffect } from 'react';
import * as React from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { DateFormatsEnum } from '../../../constants/date';
import { INTERNAL_NOTES_HINT } from '../../../constants/shipping';
import { IOneLinkChunkFormProps } from '../../../contexts/OneLinkSummaryProvider';
import { selectBusinessReasons } from '../../../store/selectors/reasons';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { formatDate, isValidDate } from '../../../utils/date';
import { isObjectsEqual } from '../../../utils/helpers';
import { isBusinessReasonRequiredInOL } from '../../../utils/oneLink';
import { isDigitalPYGReport, isDigitalReport } from '../../../utils/reports';
import { FormControls, Label } from '../../SummaryComponentMUI/components/common';
import { DateSinglePicker } from '../inputs';

const AdditionalForm = forwardRef<boolean | null, IOneLinkChunkFormProps>(({ onSubmit, instance, onClose }, ref) => {
  const reasons = useSelector(selectBusinessReasons);
  const isDigitalOL = React.useMemo(
    () =>
      isDigitalReport(instance as IOneLinkWithCampaignSummaries) ||
      isDigitalPYGReport(instance as IOneLinkWithCampaignSummaries),
    [instance],
  );

  const validationSchema = yup.object().shape({
    on_hold_until: yup.string().nullable(),
    disable_shipment_tracking: yup.boolean(),
    disable_sender_email_notifications: yup.boolean(),
    business_reason_id: yup
      .string()
      .nullable()
      .test('is-required', 'Business reason is required in campaign', (value) => {
        const noValue = !value || value.trim() === '';

        return !(isBusinessReasonRequiredInOL(instance?.pick_campaigns) && noValue);
      }),
    internal_notes: yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      on_hold_until:
        instance?.on_hold_until && isValidDate(new Date(instance?.on_hold_until)) ? instance.on_hold_until : null,
      disable_recipient_shipped_email: instance?.disable_recipient_shipped_email ?? false,
      disable_sender_email_notifications: instance?.disable_sender_email_notifications ?? true,
      business_reason_id: instance?.business_reason_id ?? '',
      internal_notes: instance?.internal_notes ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit({ additional_info: values });
      if (ref && typeof ref !== 'function') {
        ref.current = false;
      }
    },
  });

  const {
    on_hold_until,
    disable_recipient_shipped_email,
    disable_sender_email_notifications,
    business_reason_id,
    internal_notes,
  } = formik.values;
  const { handleChange, setFieldValue, handleSubmit, initialValues } = formik;

  const handleResetDate = useCallback(() => {
    setFieldValue('on_hold_until', '');
  }, [setFieldValue]);

  const handleClose = useCallback(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = false;
    }
    onClose();
  }, [ref, onClose]);

  useEffect(() => {
    if (ref && typeof ref !== 'function') {
      ref.current = !isObjectsEqual(initialValues, formik.values);
    }
  }, [formik.values, initialValues]);

  return (
    <Box
      component="form"
      role="form"
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      onSubmit={handleSubmit}
    >
      <Box>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 12 }}>
            <Label>On hold until</Label>
            <DateSinglePicker
              minDate={add(new Date(), { days: 1 })}
              maxDate={addYears(new Date(), 1)}
              value={on_hold_until || null}
              formatRule={DateFormatsEnum.yyyyMMdd}
              onSelect={(newStartDate) => {
                setFieldValue('on_hold_until', newStartDate ? formatDate(newStartDate, DateFormatsEnum.Start) : null);
              }}
              onReset={handleResetDate}
              defaultView
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 8 }}>
            <Label>Sender notifications</Label>
            <FormControlLabel
              control={
                <Switch
                  name="disable_sender_email_notifications"
                  checked={!disable_sender_email_notifications}
                  onChange={(e) => formik.setFieldValue('disable_sender_email_notifications', !e.target.checked)}
                />
              }
              label={disable_sender_email_notifications ? 'Disabled' : 'Enabled'}
            />
          </Box>
          {!isDigitalOL && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 8 }}>
              <Label>Tracking notification</Label>
              <FormControlLabel
                control={
                  <Switch
                    name="disable_recipient_shipped_email"
                    checked={!disable_recipient_shipped_email}
                    onChange={(e) => formik.setFieldValue('disable_recipient_shipped_email', !e.target.checked)}
                  />
                }
                label={disable_recipient_shipped_email ? 'Disabled' : 'Enabled'}
              />
            </Box>
          )}
        </Box>
        <TextField
          select
          label="Business reason"
          name="business_reason_id"
          value={business_reason_id}
          onChange={handleChange}
          variant="outlined"
          fullWidth
          sx={{ mb: 4 }}
          helperText={formik.errors.business_reason_id}
          error={!!formik.errors.business_reason_id}
          slotProps={{
            select: {
              MenuProps: {
                sx: {
                  '& .MuiPaper-root': {
                    maxHeight: 260,
                    overflowY: 'auto',
                  },
                },
              },
            },
            input: {
              endAdornment: (
                <span>
                  {business_reason_id?.length !== 0 && (
                    <IconButton
                      size="small"
                      sx={{ mr: 2 }}
                      onClick={() => handleChange({ target: { name: 'business_reason_id', value: '' } })}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </span>
              ),
            },
          }}
        >
          {reasons?.map((reason) => (
            <MenuItem key={reason.uid} value={reason.uid}>
              {reason.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          label="Internal notes"
          name="internal_notes"
          value={internal_notes}
          onChange={handleChange}
          variant="outlined"
          minRows={5}
          fullWidth
          sx={{ mb: 4 }}
          helperText={INTERNAL_NOTES_HINT}
        />
      </Box>
      <FormControls
        onCancel={handleClose}
        isDisabled={(ref && typeof ref !== 'function' ? !ref.current : true) || !!formik.errors.business_reason_id}
      />
    </Box>
  );
});

export default AdditionalForm;
